import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { lockScroll, unlockScroll } from '@theme/utilities';
import { getScrollEventTarget, getScrollTop, scrollContainerMediaQuery } from '@theme/scroll-container';
import { onTruckChange, truckName } from '@theme/dialed-truck';

const DESKTOP = matchMedia('(min-width: 1100px)');
const OPEN_DELAY = 90;
const CLOSE_DELAY = 140;

/**
 * <dialed-header>: sticky header with a promo bar, dropdowns and a mega menu (1100px and up),
 * and a full-screen menu with accordions below 1100px. Both layouts share one DOM: each
 * `[data-nav-item]` has a `[data-nav-toggle]` button and a `[data-nav-panel]`.
 *
 * @typedef {{ promo?: HTMLElement, bar: HTMLElement, nav: HTMLElement, scrim: HTMLElement, menuButton: HTMLButtonElement }} Refs
 * @extends {Component<Refs>}
 */
class DialedHeader extends Component {
  requiredRefs = ['bar', 'nav', 'scrim', 'menuButton'];

  /** @type {HTMLElement | null} */
  #openItem = null;
  /** @type {number | undefined} */
  #hoverTimer;
  /** @type {EventTarget | null} */
  #scrollTarget = null;
  /** @type {number | null} */
  #scrollRaf = null;
  /** @type {ResizeObserver | undefined} */
  #promoObserver;
  /** @type {() => void} */
  #unsubscribeTruck = () => {};
  #abort = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abort;

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('mouseover', this.#onMouseOver, { signal });
    this.addEventListener('mouseleave', this.#onMouseLeave, { signal });
    this.addEventListener('focusout', this.#onFocusOut, { signal });
    document.addEventListener('keydown', this.#onKeyDown, { signal });
    document.addEventListener('click', this.#onDocumentClick, { signal });
    DESKTOP.addEventListener('change', this.#onBreakpoint, { signal });
    scrollContainerMediaQuery.addEventListener('change', this.#bindScroll, { signal });
    document.addEventListener('shopify:block:select', this.#onEditorSelect, { signal });
    document.addEventListener('shopify:block:deselect', this.#onEditorDeselect, { signal });

    this.#observePromo();
    this.#bindScroll();
    this.#unsubscribeTruck = onTruckChange(this.#renderTruck);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort.abort();
    this.#abort = new AbortController();
    this.#promoObserver?.disconnect();
    this.#scrollTarget?.removeEventListener('scroll', this.#onScroll);
    this.#unsubscribeTruck();
    if (this.hasAttribute('data-menu-open')) this.closeMenu();
  }

  /* ---------- Panels ---------- */

  /** @param {HTMLElement} item */
  #toggleFor(item) {
    return /** @type {HTMLButtonElement | null} */ (item.querySelector(':scope > [data-nav-toggle]'));
  }

  /** @param {HTMLElement} item */
  openPanel(item) {
    if (this.#openItem === item) return;
    if (this.#openItem) this.#setItemState(this.#openItem, false);
    this.#openItem = item;
    this.#setItemState(item, true);
    if (DESKTOP.matches) this.toggleAttribute('data-panel-open', true);
  }

  closePanel() {
    window.clearTimeout(this.#hoverTimer);
    if (!this.#openItem) return;
    this.#setItemState(this.#openItem, false);
    this.#openItem = null;
    this.removeAttribute('data-panel-open');
  }

  closeAll() {
    this.closePanel();
    if (this.hasAttribute('data-menu-open')) this.closeMenu();
  }

  /**
   * @param {HTMLElement} item
   * @param {boolean} open
   */
  #setItemState(item, open) {
    item.toggleAttribute('data-open', open);
    this.#toggleFor(item)?.setAttribute('aria-expanded', String(open));
  }

  /* ---------- Mobile menu ---------- */

  openMenu() {
    const { nav, menuButton } = this.refs;
    this.toggleAttribute('data-menu-open', true);
    nav.setAttribute('role', 'dialog');
    nav.setAttribute('aria-modal', 'true');
    menuButton.setAttribute('aria-expanded', 'true');
    lockScroll(this);
    trapFocus(nav);
    /** @type {HTMLElement | null} */ (nav.querySelector('button, a'))?.focus();
  }

  /**
   * @param {Event | { restoreFocus?: boolean }} [options] - Pass `{ restoreFocus: false }` when
   *   something else (the cart drawer) takes focus next.
   */
  closeMenu(options) {
    const restoreFocus = !(options && 'restoreFocus' in options && options.restoreFocus === false);
    const { nav, menuButton } = this.refs;
    this.removeAttribute('data-menu-open');
    nav.removeAttribute('role');
    nav.removeAttribute('aria-modal');
    menuButton.setAttribute('aria-expanded', 'false');
    unlockScroll(this);
    removeTrapFocus();
    this.closePanel();
    if (restoreFocus && !DESKTOP.matches) menuButton.focus();
  }

  /* ---------- Events ---------- */

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const toggle = target.closest('[data-nav-toggle]');
    if (toggle) {
      const item = /** @type {HTMLElement} */ (toggle.closest('[data-nav-item]'));
      window.clearTimeout(this.#hoverTimer);
      if (this.#openItem === item) this.closePanel();
      else this.openPanel(item);
      return;
    }

    if (target.closest('[data-close-panel]')) this.closeAll();
    else if (target.closest('[data-close-menu]')) this.closeMenu({ restoreFocus: false });
  };

  /** Hover intent on desktop: ~90ms to open, instant when switching between open panels. */
  /** @param {MouseEvent} event */
  #onMouseOver = (event) => {
    if (!DESKTOP.matches || !(event.target instanceof Element)) return;
    const item = /** @type {HTMLElement | null} */ (event.target.closest('[data-nav-item]'));
    const overNav = event.target.closest('.dl-nav');

    window.clearTimeout(this.#hoverTimer);
    if (item && item !== this.#openItem) {
      const delay = this.#openItem ? 0 : OPEN_DELAY;
      this.#hoverTimer = window.setTimeout(() => this.openPanel(item), delay);
    } else if (!item && overNav === null && this.#openItem && !event.target.closest('[data-nav-panel]')) {
      this.#hoverTimer = window.setTimeout(() => this.closePanel(), CLOSE_DELAY);
    }
  };

  #onMouseLeave = () => {
    if (!DESKTOP.matches) return;
    window.clearTimeout(this.#hoverTimer);
    this.#hoverTimer = window.setTimeout(() => this.closePanel(), CLOSE_DELAY);
  };

  /** Close a desktop panel when keyboard focus moves out of it. */
  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    if (!DESKTOP.matches || !this.#openItem) return;
    const next = event.relatedTarget;
    if (next instanceof Node && this.#openItem.contains(next)) return;
    if (next === null) return;
    this.closePanel();
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    if (this.#openItem) {
      const toggle = this.#toggleFor(this.#openItem);
      this.closePanel();
      toggle?.focus();
    } else if (this.hasAttribute('data-menu-open')) {
      this.closeMenu();
    }
  };

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    if (this.#openItem && event.target instanceof Node && !this.contains(event.target)) this.closePanel();
  };

  #onBreakpoint = () => {
    this.closePanel();
    if (DESKTOP.matches && this.hasAttribute('data-menu-open')) this.closeMenu();
  };

  /** Theme editor: open the panel of a selected nav block. */
  /** @param {Event} event */
  #onEditorSelect = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const item = /** @type {HTMLElement | null} */ (target?.closest('[data-nav-item]') ?? null);
    if (item && this.contains(item)) this.openPanel(item);
  };

  #onEditorDeselect = () => this.closePanel();

  /* ---------- Scroll state + promo height ---------- */

  #observePromo() {
    const promo = this.refs.promo;
    const section = this.closest('.header-section');
    if (!(section instanceof HTMLElement)) return;
    if (!promo) {
      section.style.removeProperty('--dl-promo-height');
      return;
    }
    this.#promoObserver = new ResizeObserver(() => {
      section.style.setProperty('--dl-promo-height', `${promo.offsetHeight}px`);
    });
    this.#promoObserver.observe(promo);
  }

  #bindScroll = () => {
    this.#scrollTarget?.removeEventListener('scroll', this.#onScroll);
    this.#scrollTarget = getScrollEventTarget();
    this.#scrollTarget.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#updateScrolled();
  };

  #onScroll = () => {
    if (this.#scrollRaf !== null) return;
    this.#scrollRaf = requestAnimationFrame(() => {
      this.#scrollRaf = null;
      this.#updateScrolled();
    });
  };

  #updateScrolled() {
    const scrolled = getScrollTop() > (this.refs.promo?.offsetHeight ?? 0) + 8;
    if (this.dataset.scrolled !== String(scrolled)) {
      this.dataset.scrolled = String(scrolled);
      this.dataset.stickyState = scrolled ? 'active' : 'inactive';
    }
  }

  /* ---------- Selected truck ---------- */

  /** @param {import('@theme/dialed-truck').Truck | null} truck */
  #renderTruck = (truck) => {
    for (const chip of this.querySelectorAll('[data-truck-chip]')) {
      const name = chip.querySelector('[data-truck-name]');
      if (name) name.textContent = truck ? truckName(truck) : '';
      if (chip instanceof HTMLElement) {
        chip.hidden = !truck;
        if (truck?.label) chip.title = truck.label;
      }
    }
    for (const line of this.querySelectorAll('[data-truck-line]')) {
      if (!(line instanceof HTMLElement)) continue;
      line.dataset.default ??= line.textContent ?? '';
      line.textContent = truck ? (line.dataset.template ?? '').replace('[truck]', truckName(truck)) : line.dataset.default;
    }
  };
}

if (!customElements.get('dialed-header')) customElements.define('dialed-header', DialedHeader);
